import { ExclamationCircleOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import { PageTopBar } from '~components/page-top-bar';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useOnboardingStore,
} from '~contexts/mobx';
import * as Colors from '~styles/colors';
import { handleDateParamsUpdate } from '~utils/date-picker-helpers';
import { useNavigation } from '~utils/use-navigation';
import { useSearchParams } from '~utils/use-search-params';
import { AcquisitionFunnelSection } from '~onboarding-components/AcquisitionFunnelSection';
import { OnboardingStepsSection } from '~onboarding-components/OnboardingStepsSection';
import { SegmentationSurveyChartSection } from '~onboarding-components/SegmentationSurveyChartSection';
import style from './style.module.scss';
import { AnalyticsActionBar } from '~containers/analytics/components/action-bar';
import { useRegionProvinces } from '~components/analytics-search-options/region-provinces/use-region-provinces';

type OnboardingQueryParams = {
    startDate?: string;
    endDate?: string;
    provinces?: string | string[];
};

export const OnboardingPage = observer(() => {
    const authStore = useAuthStore();
    const onboardingStore = useOnboardingStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const navigation = useNavigation();
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [isComparing, setIsComparing] = useState<boolean>(false);
    const search = useSearchParams<OnboardingQueryParams>();
    const {
        isQcTarget,
        provincesParam,
        selectedProvinces,
        setSelectedProvinces,
    } = useRegionProvinces({
        appTarget: applicationSelectionStore.appTarget,
        defaultProvincesCsv:
            applicationSelectionStore.getProvincesFromSelectedApplication,
        queryProvinces: search.params.provinces,
        setProvincesParam: (value) => search.setParam('provinces', value),
    });
    const { startDate: startDateParam, endDate: endDateParam } = search.params;

    useEffect(() => {
        if (
            !authStore.hasManagementAccess &&
            !authStore.isCustomerServiceAgent
        ) {
            navigation.goToHome();
        }
    }, [
        authStore.hasManagementAccess,
        authStore.isCustomerServiceAgent,
        navigation,
    ]);

    useEffect(() => {
        if (startDateParam && endDateParam) {
            const startDate = moment(startDateParam).utc().toISOString();
            const endDate = moment(endDateParam).utc().toISOString();
            const appTarget = applicationSelectionStore.appTarget;

            const onboardingParams = {
                startDate,
                endDate,
                appTarget,
                provinces: provincesParam,
            };

            onboardingStore.fetchSegmentationData(onboardingParams);
            onboardingStore.fetchOnboardingData(onboardingParams);
            onboardingStore.fetchProgressionData(onboardingParams);

            const diff =
                moment(endDateParam).diff(moment(startDateParam), 'days') + 1;
            const priorStart = moment(startDateParam)
                .subtract(diff, 'days')
                .utc()
                .toISOString();
            const priorEnd = moment(startDateParam)
                .subtract(1, 'days')
                .utc()
                .toISOString();

            const priorParams = {
                startDate: priorStart,
                endDate: priorEnd,
                appTarget,
                periodKey: 'previousPeriod',
                provinces: provincesParam,
            };

            onboardingStore.fetchSegmentationData(priorParams);
            onboardingStore.fetchOnboardingData(priorParams);
            onboardingStore.fetchProgressionData(priorParams);
        }
    }, [
        startDateParam,
        endDateParam,
        onboardingStore,
        applicationSelectionStore.appTarget,
        provincesParam,
    ]);

    useEffect(() => {
        if (startDateParam && endDateParam) {
            const startDate = moment(startDateParam).utc().toISOString();
            const endDate = moment(endDateParam).utc().toISOString();
            const appTarget = applicationSelectionStore.appTarget;

            const diff =
                moment(endDateParam).diff(moment(startDateParam), 'days') + 1;
            const priorStart = moment(startDateParam)
                .subtract(diff, 'days')
                .utc()
                .toISOString();
            const priorEnd = moment(startDateParam)
                .subtract(1, 'days')
                .utc()
                .toISOString();

            onboardingStore.fetchAppDownloadsComparison(
                {
                    startDate,
                    endDate,
                    appTarget,
                },
                {
                    startDate: priorStart,
                    endDate: priorEnd,
                    appTarget,
                    periodKey: 'previousPeriod',
                }
            );
        }
    }, [
        startDateParam,
        endDateParam,
        onboardingStore,
        applicationSelectionStore.appTarget,
    ]);

    useEffect(() => {
        setShowWarning(!(startDateParam && endDateParam));
    }, [startDateParam, endDateParam]);

    const regionFilter = {
        value: selectedProvinces,
        disabled: isQcTarget,
        onChange: setSelectedProvinces,
    };

    return (
        <>
            <PageTopBar title="Onboarding Data" />

            <div className={`${style.vertical_container} content_with_margins`}>
                <AnalyticsActionBar stacked>
                    <AnalyticsSearchOptions
                        onCompareChange={setIsComparing}
                        updateParams={(dates) => {
                            handleDateParamsUpdate(dates, search.setParams);
                        }}
                        startDate={search.params.startDate}
                        endDate={search.params.endDate}
                        regionFilter={regionFilter}
                    />
                </AnalyticsActionBar>

                {showWarning && (
                    <div className={style.warning_banner}>
                        <ExclamationCircleOutlined
                            style={{
                                fontSize: '24px',
                                color: Colors.UNKNOWN_ANT_ORANGE,
                            }}
                        />
                        <span>Please select a valid date range.</span>
                    </div>
                )}

                <AcquisitionFunnelSection isComparing={isComparing} />
                <SegmentationSurveyChartSection isComparing={isComparing} />
                <OnboardingStepsSection isComparing={isComparing} />
            </div>
        </>
    );
});
